import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Breadcrumbs,
  Link as MuiLink
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import AuthLayout from '../components/AuthLayout';
import userService, { User, UserFormData } from '../services/user.service';
import { useAuth } from '../contexts/AuthContext';

// Esquema de validação do formulário
const validationSchema = Yup.object({
  email: Yup.string()
    .email('Email inválido')
    .required('Email é obrigatório'),
  displayName: Yup.string()
    .required('Nome é obrigatório')
    .min(3, 'Nome deve ter pelo menos 3 caracteres'),
  role: Yup.string()
    .oneOf(['admin', 'manager', 'field-agent', 'user'], 'Perfil inválido')
    .required('Perfil é obrigatório'),
  password: Yup.string()
    .when('isNew', {
      is: true,
      then: (schema) => schema
        .required('Senha é obrigatória para novos usuários')
        .min(6, 'Senha deve ter pelo menos 6 caracteres'),
      otherwise: (schema) => schema
    }),
  confirmPassword: Yup.string()
    .when('password', {
      is: (val: string) => val && val.length > 0,
      then: (schema) => schema
        .required('Confirme a senha')
        .oneOf([Yup.ref('password')], 'As senhas não conferem'),
      otherwise: (schema) => schema
    })
});

const UserForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const isEditMode = id && id !== 'new';

  // Inicializar formulário com Formik
  const formik = useFormik({
    initialValues: {
      email: '',
      displayName: '',
      role: 'user',
      isActive: true,
      password: '',
      confirmPassword: '',
      isNew: !isEditMode
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);
      setSuccess(null);

      try {
        // Preparar dados para envio
        const userData: UserFormData = {
          email: values.email,
          displayName: values.displayName,
          role: values.role as 'admin' | 'manager' | 'field-agent' | 'user',
          isActive: values.isActive
        };

        if (isEditMode) {
          // Modo de edição - atualizar usuário existente
          await userService.updateUser(id, userData);
          setSuccess('Usuário atualizado com sucesso!');
          setTimeout(() => navigate('/users'), 1500);
        } else {
          // Modo de criação - criar usuário via backend (que usa Firebase Admin SDK)
          // Adicionar senha aos dados do usuário
          const userDataWithPassword = {
            ...userData,
            password: values.password
          };
          
          await userService.createUser(userDataWithPassword);
          setSuccess('Usuário criado com sucesso!');
          setTimeout(() => navigate('/users'), 1500);
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Erro ao salvar usuário');
        console.error('Erro ao salvar usuário:', err);
      } finally {
        setLoading(false);
      }
    },
  });

  // Carregar dados do usuário para edição
  useEffect(() => {
    const loadUser = async () => {
      if (isEditMode) {
        setInitialLoading(true);
        try {
          const userData = await userService.getUserById(id);
          formik.setValues({
            ...formik.values,
            email: userData.email,
            displayName: userData.displayName || '',
            role: userData.role,
            isActive: userData.isActive,
            isNew: false,
            password: '',
            confirmPassword: ''
          });
        } catch (err: any) {
          setError(err.response?.data?.message || 'Erro ao carregar dados do usuário');
          console.error('Erro ao carregar dados do usuário:', err);
        } finally {
          setInitialLoading(false);
        }
      }
    };

    loadUser();
  }, [id, isEditMode]);

  // Mapeamento de perfis para exibição em português
  const roleOptions = [
    { value: 'admin', label: 'Administrador' },
    { value: 'manager', label: 'Comercial' },
    { value: 'field-agent', label: 'Técnico' },
    { value: 'user', label: 'Usuário' }
  ];

  return (
    <AuthLayout title="Formulário de Usuário">
      <Box sx={{ p: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <MuiLink 
            component="button" 
            variant="body1" 
            onClick={() => navigate('/users')}
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <ArrowBackIcon fontSize="small" sx={{ mr: 0.5 }} />
            Voltar para lista de usuários
          </MuiLink>
          <Typography color="text.primary">
            {isEditMode ? 'Editar Usuário' : 'Novo Usuário'}
          </Typography>
        </Breadcrumbs>

        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
          {isEditMode ? 'Editar Usuário' : 'Novo Usuário'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {initialLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper sx={{ p: 3 }}>
            <form onSubmit={formik.handleSubmit}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  label="Email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  disabled={isEditMode ? true : false} // Email não pode ser alterado no modo de edição
                  margin="normal"
                />

                <TextField
                  fullWidth
                  id="displayName"
                  name="displayName"
                  label="Nome"
                  value={formik.values.displayName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.displayName && Boolean(formik.errors.displayName)}
                  helperText={formik.touched.displayName && formik.errors.displayName}
                  margin="normal"
                />

                <FormControl 
                  fullWidth 
                  margin="normal"
                  error={formik.touched.role && Boolean(formik.errors.role)}
                >
                  <InputLabel id="role-label">Perfil</InputLabel>
                  <Select
                    labelId="role-label"
                    id="role"
                    name="role"
                    value={formik.values.role}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Perfil"
                  >
                    {roleOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.role && formik.errors.role && (
                    <FormHelperText>{formik.errors.role}</FormHelperText>
                  )}
                </FormControl>

                <FormControlLabel
                  control={
                    <Switch
                      checked={formik.values.isActive}
                      onChange={(e) => formik.setFieldValue('isActive', e.target.checked)}
                      name="isActive"
                      color="primary"
                    />
                  }
                  label="Usuário ativo"
                  sx={{ mt: 2 }}
                />
              </Box>

              {!isEditMode && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
                  <TextField
                    fullWidth
                    id="password"
                    name="password"
                    label="Senha"
                    type="password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.password && Boolean(formik.errors.password)}
                    helperText={formik.touched.password && formik.errors.password}
                    margin="normal"
                  />

                  <TextField
                    fullWidth
                    id="confirmPassword"
                    name="confirmPassword"
                    label="Confirmar Senha"
                    type="password"
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                    helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                    margin="normal"
                  />
                </Box>
              )}

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/users')}
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Salvar'}
                </Button>
              </Box>
            </form>
          </Paper>
        )}
      </Box>
    </AuthLayout>
  );
};

export default UserForm;
